import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Layout } from "@/components/Layout";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Skeleton } from "@/components/ui/skeleton";
import { useT } from "@/i18n/I18nContext";
import { getLosses, getLossReasons, addLossReason, deleteLossReason, revertLoss, downloadLossesXlsx, fmt } from "@/lib/api";
import { Trash2, Undo2, Scale, Banknote, ListChecks, Settings2, Plus, X, CheckCircle2, Download } from "lucide-react";
import { toast } from "sonner";

const ALL = "__all__";
const BUILTIN = ["expired", "damaged", "quality", "other", "unspecified"];

const monthStart = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-01`;
};
const today = () => new Date().toISOString().slice(0, 10);

// Per-reason accent colors (design tokens); custom reasons share a neutral accent.
const REASON_COLOR = {
  expired: "hsl(var(--warning))",
  damaged: "hsl(var(--danger))",
  quality: "hsl(var(--info))",
  unspecified: "hsl(var(--muted-foreground))",
  other: "hsl(var(--muted-foreground))",
};
const customColor = "hsl(var(--brand))";
const slug = (s) => String(s).toLowerCase().replace(/[^a-z0-9]+/gi, "-");

export default function Losses() {
  const { t, td } = useT();
  const [start, setStart] = useState(monthStart());
  const [end, setEnd] = useState(today());
  const [product, setProduct] = useState(ALL);
  const [reason, setReason] = useState(ALL);
  const [data, setData] = useState(null);
  const [products, setProducts] = useState([]);
  const [reasons, setReasons] = useState({ builtin: [], custom: [] });
  const [loading, setLoading] = useState(true);
  const [revertTarget, setRevertTarget] = useState(null);
  const [reverting, setReverting] = useState(false);
  const [manageOpen, setManageOpen] = useState(false);
  const [exporting, setExporting] = useState(false);

  const onExport = async () => {
    setExporting(true);
    try {
      await downloadLossesXlsx(params);
    } catch {
      toast.error(t("losses.exportError"));
    } finally {
      setExporting(false);
    }
  };

  const reasonLabel = useCallback(
    (r) => (BUILTIN.includes(r) ? t(`stockMovements.losses.reason.${r}`) : r),
    [t],
  );
  const reasonColor = (r) => REASON_COLOR[r] || customColor;

  const params = useMemo(() => ({
    start, end,
    ...(product === ALL ? {} : { product }),
    ...(reason === ALL ? {} : { reason }),
  }), [start, end, product, reason]);

  const load = useCallback(() => {
    setLoading(true);
    getLosses(params)
      .then((d) => {
        setData(d);
        if (Array.isArray(d.products) && product === ALL && reason === ALL) setProducts(d.products);
        else setProducts((prev) => (prev.length ? prev : d.products || []));
      })
      .catch(() => { setData(null); toast.error(t("losses.loadError")); })
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { getLossReasons().then(setReasons).catch(() => {}); }, []);

  const onRevert = async () => {
    if (!revertTarget) return;
    setReverting(true);
    try {
      await revertLoss(revertTarget.id);
      toast.success(t("losses.revertDone", { code: revertTarget.lot_code }));
      setRevertTarget(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("losses.revertError"));
    } finally {
      setReverting(false);
    }
  };

  const totals = data?.totals || { kg: 0, value: 0, count: 0, reverted_count: 0 };
  const items = data?.items || [];
  const currency = data?.currency || "RON";
  const allReasonOptions = [...(reasons.builtin || []), ...(reasons.custom || []), "unspecified"];

  return (
    <Layout
      actions={
        <div className="flex items-center gap-2">
          <Button size="sm" variant="outline" onClick={onExport} disabled={exporting || loading}
            data-testid="losses-export-xlsx" className="gap-1.5">
            <Download className="h-4 w-4" /> {t("losses.exportXlsx")}
          </Button>
          <Button size="sm" variant="outline" onClick={() => setManageOpen(true)} data-testid="losses-manage-reasons" className="gap-1.5">
            <Settings2 className="h-4 w-4" /> {t("losses.manageReasons")}
          </Button>
        </div>
      }
    >
      <div className="mb-5">
        <h1 className="flex items-center gap-2 text-2xl font-semibold tracking-tight">
          <Trash2 className="h-6 w-6 text-[hsl(var(--danger))]" /> {t("losses.title")}
        </h1>
        <p className="mt-1 max-w-2xl text-sm text-muted-foreground">{t("losses.subtitle")}</p>
      </div>

      {/* Filters */}
      <Card className="mb-4">
        <CardContent className="flex flex-col gap-3 p-4 sm:flex-row sm:flex-wrap sm:items-end">
          <div className="space-y-1.5">
            <Label className="text-xs text-muted-foreground">{t("stockMovements.from")}</Label>
            <Input type="date" value={start} max={end} onChange={(e) => setStart(e.target.value)}
              className="h-9 w-[160px] text-sm" data-testid="losses-start" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs text-muted-foreground">{t("stockMovements.to")}</Label>
            <Input type="date" value={end} min={start} onChange={(e) => setEnd(e.target.value)}
              className="h-9 w-[160px] text-sm" data-testid="losses-end" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs text-muted-foreground">{t("common.product")}</Label>
            <Select value={product} onValueChange={setProduct}>
              <SelectTrigger className="h-9 w-[190px] text-sm" data-testid="losses-product-filter">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={ALL}>{t("stockMovements.allProducts")}</SelectItem>
                {products.map((p) => <SelectItem key={p} value={p}>{td(p)}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs text-muted-foreground">{t("losses.reasonCol")}</Label>
            <Select value={reason} onValueChange={setReason}>
              <SelectTrigger className="h-9 w-[190px] text-sm" data-testid="losses-reason-filter">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={ALL}>{t("losses.allReasons")}</SelectItem>
                {allReasonOptions.map((r) => (
                  <SelectItem key={r} value={r} data-testid={`losses-reason-option-${slug(r)}`}>{reasonLabel(r)}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Summary cards */}
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-3">
        <Card data-testid="losses-card-kg">
          <CardContent className="p-5">
            <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
              <Scale className="h-4 w-4 text-[hsl(var(--danger))]" /> {t("losses.totalLost")}
            </div>
            <div className="mt-2 font-mono text-2xl font-semibold tabular-nums text-[hsl(var(--danger))]" data-testid="losses-total-kg-card">
              {fmt(totals.kg, 2)} <span className="text-sm font-normal text-muted-foreground">kg</span>
            </div>
          </CardContent>
        </Card>
        <Card data-testid="losses-card-value">
          <CardContent className="p-5">
            <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
              <Banknote className="h-4 w-4 text-[hsl(var(--danger))]" /> {t("losses.estValue")}
            </div>
            <div className="mt-2 font-mono text-2xl font-semibold tabular-nums text-[hsl(var(--danger))]" data-testid="losses-total-value-card">
              ~{fmt(totals.value, 2)} <span className="text-sm font-normal text-muted-foreground">{currency}</span>
            </div>
          </CardContent>
        </Card>
        <Card data-testid="losses-card-count">
          <CardContent className="p-5">
            <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
              <ListChecks className="h-4 w-4" /> {t("losses.writeOffCount")}
            </div>
            <div className="mt-2 font-mono text-2xl font-semibold tabular-nums" data-testid="losses-total-count-card">
              {totals.count}
            </div>
            {totals.reverted_count > 0 && (
              <div className="mt-1 text-xs text-muted-foreground" data-testid="losses-reverted-count">
                {t("losses.revertedCount", { n: totals.reverted_count })}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Loss events table */}
      <Card className="mt-4">
        <CardContent className="pt-5">
          <div className="overflow-x-auto thin-scroll">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t("stockMovements.date")}</TableHead>
                  <TableHead>{t("stockMovements.lot")}</TableHead>
                  <TableHead>{t("common.product")}</TableHead>
                  <TableHead>{t("losses.reasonCol")}</TableHead>
                  <TableHead className="text-right">{t("stockMovements.qty")}</TableHead>
                  <TableHead className="text-right">{t("stockMovements.kg")}</TableHead>
                  <TableHead className="text-right">{t("losses.valueCol")}</TableHead>
                  <TableHead>{t("losses.byWhom")}</TableHead>
                  <TableHead className="text-right">{t("common.actions")}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  Array.from({ length: 5 }).map((_, i) => (
                    <TableRow key={i}>
                      {Array.from({ length: 9 }).map((__, j) => (
                        <TableCell key={j}><Skeleton className="h-4 w-full" /></TableCell>
                      ))}
                    </TableRow>
                  ))
                ) : items.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={9} className="py-10 text-center text-sm text-muted-foreground" data-testid="losses-empty">
                      <CheckCircle2 className="mx-auto mb-2 h-8 w-8 text-[hsl(var(--success))]/50" />
                      {t("losses.empty")}
                    </TableCell>
                  </TableRow>
                ) : items.map((it) => (
                  <TableRow key={it.id} data-testid={`loss-row-${it.id}`}
                    className={it.reverted ? "opacity-50" : ""}>
                    <TableCell className="whitespace-nowrap font-mono text-xs tabular-nums">{it.date}</TableCell>
                    <TableCell className="font-mono text-xs">{it.lot_code || "—"}</TableCell>
                    <TableCell className="font-medium">{td(it.product)}</TableCell>
                    <TableCell>
                      <Badge className="gap-1.5 border-transparent text-[10px]"
                        style={{ backgroundColor: `color-mix(in srgb, ${reasonColor(it.reason)} 14%, transparent)`, color: reasonColor(it.reason) }}
                        data-testid={`loss-row-reason-${it.id}`}>
                        <span className="inline-block h-1.5 w-1.5 rounded-full" style={{ backgroundColor: reasonColor(it.reason) }} />
                        {reasonLabel(it.reason)}
                      </Badge>
                      {it.note && <div className="mt-0.5 max-w-[220px] truncate text-[11px] text-muted-foreground" title={it.note}>{it.note}</div>}
                    </TableCell>
                    <TableCell className="whitespace-nowrap text-right font-mono tabular-nums">{fmt(it.qty, 2)} {it.unit}</TableCell>
                    <TableCell className="text-right font-mono tabular-nums text-[hsl(var(--danger))]">-{fmt(it.qty_kg, 2)}</TableCell>
                    <TableCell className="whitespace-nowrap text-right font-mono tabular-nums">
                      {it.value != null ? `~${fmt(it.value, 2)} ${currency}` : "—"}
                    </TableCell>
                    <TableCell className="max-w-[140px] truncate text-xs text-muted-foreground">{it.user}</TableCell>
                    <TableCell className="text-right">
                      {it.reverted ? (
                        <Badge variant="outline" className="text-[10px]" data-testid={`loss-reverted-badge-${it.id}`}>
                          {t("losses.reverted")}
                        </Badge>
                      ) : (
                        <Button variant="ghost" size="sm" className="h-8 gap-1 text-xs"
                          onClick={() => setRevertTarget(it)} data-testid={`loss-revert-${it.id}`}>
                          <Undo2 className="h-3.5 w-3.5" /> {t("losses.undo")}
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
          {items.length > 0 && (
            <p className="mt-3 text-xs text-muted-foreground">{t("losses.tableNote")}</p>
          )}
        </CardContent>
      </Card>

      {/* Undo confirmation */}
      <AlertDialog open={!!revertTarget} onOpenChange={(o) => { if (!o) setRevertTarget(null); }}>
        <AlertDialogContent data-testid="loss-revert-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>{t("losses.revertConfirmTitle")}</AlertDialogTitle>
            <AlertDialogDescription>
              {revertTarget && t("losses.revertConfirmText", {
                qty: fmt(revertTarget.qty, 2),
                unit: revertTarget.unit,
                code: revertTarget.lot_code,
                product: td(revertTarget.product),
              })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="loss-revert-cancel">{t("common.cancel")}</AlertDialogCancel>
            <AlertDialogAction onClick={onRevert} disabled={reverting} data-testid="loss-revert-confirm">
              {t("losses.undo")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <ManageReasonsDialog open={manageOpen} onClose={() => setManageOpen(false)}
        reasons={reasons} setReasons={setReasons} reasonLabel={reasonLabel} />
    </Layout>
  );
}

/** Admin dialog: built-in reasons are fixed; custom reasons can be added/removed freely.
    Removing a custom reason only hides it from the picker — past write-offs keep it. */
function ManageReasonsDialog({ open, onClose, reasons, setReasons, reasonLabel }) {
  const { t } = useT();
  const [label, setLabel] = useState("");
  const [busy, setBusy] = useState(false);

  const onAdd = async () => {
    const v = label.trim();
    if (!v) return;
    setBusy(true);
    try {
      const d = await addLossReason(v);
      setReasons(d);
      setLabel("");
      toast.success(t("losses.reasonAdded"));
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("losses.reasonAddError"));
    } finally {
      setBusy(false);
    }
  };
  const onDelete = async (r) => {
    setBusy(true);
    try {
      const d = await deleteLossReason(r);
      setReasons(d);
      toast.success(t("losses.reasonDeleted"));
    } catch {
      toast.error(t("losses.reasonAddError"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent data-testid="manage-reasons-dialog">
        <DialogHeader>
          <DialogTitle>{t("losses.manageReasons")}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <Label className="text-xs text-muted-foreground">{t("losses.builtinReasons")}</Label>
            <div className="mt-1.5 flex flex-wrap gap-1.5">
              {(reasons.builtin || []).map((r) => (
                <Badge key={r} variant="secondary" className="text-xs">{reasonLabel(r)}</Badge>
              ))}
            </div>
          </div>
          <div>
            <Label className="text-xs text-muted-foreground">{t("losses.customReasons")}</Label>
            {(reasons.custom || []).length === 0 ? (
              <p className="mt-1.5 text-sm text-muted-foreground" data-testid="custom-reasons-empty">{t("losses.noCustomReasons")}</p>
            ) : (
              <div className="mt-1.5 flex flex-wrap gap-1.5">
                {(reasons.custom || []).map((r) => (
                  <Badge key={r} variant="outline" className="gap-1 pr-1 text-xs" data-testid={`custom-reason-${slug(r)}`}>
                    {r}
                    <button type="button" onClick={() => onDelete(r)} disabled={busy}
                      className="rounded-full p-0.5 hover:bg-secondary" aria-label={t("common.delete")}
                      data-testid={`custom-reason-delete-${slug(r)}`}>
                      <X className="h-3 w-3" />
                    </button>
                  </Badge>
                ))}
              </div>
            )}
          </div>
          <div className="flex items-end gap-2">
            <div className="flex-1 space-y-1.5">
              <Label htmlFor="new-reason">{t("losses.newReason")}</Label>
              <Input id="new-reason" value={label} maxLength={40}
                onChange={(e) => setLabel(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); onAdd(); } }}
                placeholder={t("losses.newReasonPh")} data-testid="new-reason-input" />
            </div>
            <Button onClick={onAdd} disabled={busy || !label.trim()} className="gap-1" data-testid="new-reason-add">
              <Plus className="h-4 w-4" /> {t("common.add")}
            </Button>
          </div>
          <p className="text-xs text-muted-foreground">{t("losses.manageHint")}</p>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="manage-reasons-close">{t("common.close")}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
